/**
 * 客戶操作台（REQ-20260418015）。
 *
 * 替代舊的 List / Detail / Edit / New 四個頁面，把原系統「單頁操作台」UX 還原：
 * - 頂部工具列：釘選 / + 新增 / 儲存 / 刪除 / 取消 / 上一筆 / 下一筆 / 搜尋（REQ-20260916005）
 * - 中段：客戶欄位（inline editable；權限不足者 readonly）
 * - 下段：訪談時間軸（點新增/編輯 → InterviewModal）
 * - 鍵盤 +/- 翻頁（見 useNeighborHotkeys）
 * - 搜尋 Modal 選定後，以該欄位為 sortBy 載入該筆，翻頁即沿該欄位排序
 *
 * URL 狀態：
 *   /customers/console?mode=new
 *   /customers/console?id=<n>&sortBy=<field>&order=asc|desc
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  CreateCustomerInput,
  CustomerSearchableField,
  CustomerSortableField,
} from '@app/shared';
import { CUSTOMER_SEARCHABLE_FIELDS, CUSTOMER_SORTABLE_FIELDS } from '@app/shared';
import { useAuthStore } from '../features/auth/store';
import {
  useCreateCustomer,
  useCustomer,
  useCustomerNeighbor,
  useDeleteCustomer,
  useUpdateCustomer,
} from '../features/customers/api';
import {
  CustomerForm,
  type CustomerFormHandle,
} from '../features/customers/CustomerForm';
import { SearchModal } from '../features/customers/SearchModal';
import { DeleteCustomerDialog } from '../features/customers/DeleteCustomerDialog';
import { useNeighborHotkeys } from '../features/customers/useNeighborHotkeys';
import { InterviewTimeline } from '../features/interviews/InterviewTimeline';
import { useCustomers } from '../features/customers/api';
import { useInterviewsTotalAll } from '../features/interviews/api';
import { InventoryTimeline } from '../features/inventory/InventoryTimeline';
import { useInventoryRecordsTotal } from '../features/inventory/api';
import { ReminderPanel } from '../features/party-reminders/ReminderPanel';
import { useOpenReminders } from '../features/party-reminders/api';
import { TabButton } from '../components/TabButton';
import { CustomerLineContactsPanel } from '../features/line/CustomerLineContactsPanel';
import { CustomerArPanel } from '../features/settlements/CustomerArPanel';
import { useCustomerArSettlementsTotal } from '../features/settlements/api';
import { useLineContactsByCustomer } from '../features/line/lineApi';
import { useIsPinned, useTogglePin } from '../features/customer-pins/api';
import { setLastConsoleUrl } from '../components/CustomersNavLink';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CUSTOMER_FIELD_LABELS } from '../features/customers/field-labels';
import { getApiErrorMessage } from '../lib/api-client';

const DEFAULT_SORT: CustomerSortableField = 'cCode';

function parseSort(raw: string | null): CustomerSortableField {
  if (raw && (CUSTOMER_SORTABLE_FIELDS as readonly string[]).includes(raw)) {
    return raw as CustomerSortableField;
  }
  return DEFAULT_SORT;
}

function parseSearchField(raw: string | null): CustomerSearchableField | null {
  if (raw && (CUSTOMER_SEARCHABLE_FIELDS as readonly string[]).includes(raw)) {
    return raw as CustomerSearchableField;
  }
  return null;
}

export function CustomerConsolePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAuthStore((s) => s.user);

  const mode = searchParams.get('mode') === 'new' ? 'new' : 'view';
  const rawId = searchParams.get('id');
  const id = rawId ? Number(rawId) : undefined;
  const sortBy = parseSort(searchParams.get('sortBy'));
  const highlightInterviewIdRaw = searchParams.get('interviewId');
  const highlightInterviewId = highlightInterviewIdRaw
    ? Number(highlightInterviewIdRaw)
    : undefined;
  const order: 'asc' | 'desc' =
    searchParams.get('order') === 'desc' ? 'desc' : 'asc';

  // REQ-20260421009 §1:scoped paging 的搜尋 context(field + q)
  const searchField = parseSearchField(searchParams.get('field'));
  const searchQ = searchParams.get('q')?.trim() ?? '';
  const hasSearchContext = searchField != null && searchQ.length > 0;

  const formRef = useRef<CustomerFormHandle>(null);
  const [dirty, setDirty] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // REQ-20260916004:「取消」在新增模式要退回剛剛看的那一筆,所以要記住它
  const lastViewedIdRef = useRef<number | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // REQ-20260421010:訪談 / 存貨 tab 切換;REQ-20260518002 加入 LINE 綁定 tab;
  // REQ-20260918002 加入提醒事項 tab。切換客戶時重置
  const [activeTab, setActiveTab] = useState<
    'reminders' | 'interviews' | 'inventory' | 'line' | 'ar'
  >('interviews');
  // 預設**仍是訪談紀錄**:有未完成的提醒只讓分頁變琥珀色,不搶走使用者原本要看的東西
  useEffect(() => setActiveTab('interviews'), [id]);
  const interviewsTotalQuery = useInterviewsTotalAll(mode === 'view' ? id : undefined, 'customer');
  const inventoryTotalQuery = useInventoryRecordsTotal(mode === 'view' ? id : undefined);
  const openRemindersQuery = useOpenReminders('customer', mode === 'view' ? id : undefined);
  const lineContactsQuery = useLineContactsByCustomer(mode === 'view' ? id : undefined);
  const arTotalQuery = useCustomerArSettlementsTotal(mode === 'view' ? id : undefined);
  const pinnedQuery = useIsPinned(mode === 'view' ? id : undefined);
  const togglePinMutation = useTogglePin(id);

  // 若沒指定 id 且不是新增模式：載入第一筆（依 sortBy）作為預設
  const firstQuery = useCustomers({
    page: 1,
    pageSize: 1,
    sort: sortBy,
    order,
  });
  useEffect(() => {
    if (mode === 'new') return;
    if (id != null) return;
    const first = firstQuery.data?.data?.[0];
    if (first) {
      const next = new URLSearchParams(searchParams);
      next.set('id', String(first.id));
      next.set('sortBy', sortBy);
      next.set('order', order);
      setSearchParams(next, { replace: true });
    }
  }, [mode, id, firstQuery.data, searchParams, setSearchParams, sortBy, order]);

  // REQ-20260421008 §5:檢視某位客戶時,把當下 URL 記到 sessionStorage,
  // 讓導覽列「客戶」連結(CustomersNavLink)可以回到此位置而非跳回第一筆。
  useEffect(() => {
    if (mode !== 'view' || id == null) return;
    setLastConsoleUrl(`/customers/console?${searchParams.toString()}`);
  }, [mode, id, searchParams]);

  useEffect(() => {
    if (mode !== 'view' || id == null) return;
    lastViewedIdRef.current = id;
  }, [mode, id]);

  const customerQuery = useCustomer(mode === 'view' ? id : undefined);
  const current = customerQuery.data?.data;
  const canWrite =
    me?.role === 'ADMIN' ||
    (me?.role === 'SALES' &&
      (mode === 'new' || (current != null && current.ownerId === me?.id)));
  const readOnly = mode === 'view' && !canWrite;

  const prevQuery = useCustomerNeighbor(
    mode === 'view' ? id : undefined,
    {
      direction: 'previous',
      sortBy,
      order,
      ...(hasSearchContext ? { field: searchField, q: searchQ } : {}),
    },
  );
  const nextQuery = useCustomerNeighbor(
    mode === 'view' ? id : undefined,
    {
      direction: 'next',
      sortBy,
      order,
      ...(hasSearchContext ? { field: searchField, q: searchQ } : {}),
    },
  );

  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer(id ?? 0);
  const deleteMutation = useDeleteCustomer();

  const goTo = (nextId: number): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('mode');
    next.set('id', String(nextId));
    next.set('sortBy', sortBy);
    next.set('order', order);
    setSearchParams(next);
  };

  const confirmLeave = (): boolean => {
    if (!dirty) return true;
    return window.confirm('有未儲存的變更，是否放棄？');
  };

  const handlePrev = (): void => {
    const neighborId = prevQuery.data?.id;
    if (neighborId == null) return;
    if (!confirmLeave()) return;
    goTo(neighborId);
  };
  const handleNext = (): void => {
    const neighborId = nextQuery.data?.id;
    if (neighborId == null) return;
    if (!confirmLeave()) return;
    goTo(neighborId);
  };

  useNeighborHotkeys({
    onNext: handleNext,
    onPrevious: handlePrev,
    enabled: mode === 'view' && !searchOpen,
  });

  const handleSave = (): void => {
    formRef.current?.submit();
  };

  const handleSubmit = (values: CreateCustomerInput): void => {
    if (mode === 'new') {
      createMutation.mutate(values, {
        onSuccess: (created) => {
          setDirty(false);
          goTo(created.id);
        },
      });
    } else if (id != null) {
      updateMutation.mutate(values, {
        onSuccess: () => {
          setDirty(false);
        },
      });
    }
  };

  const openDeleteDialog = (): void => {
    if (mode !== 'view' || id == null || !current) return;
    setDeleteOpen(true);
  };

  const handleConfirmDelete = (): void => {
    if (mode !== 'view' || id == null || !current) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setDeleteOpen(false);
        // 刪除後移到下一筆；若沒有下一筆則回 console 預設
        const nextId = nextQuery.data?.id ?? prevQuery.data?.id;
        if (nextId != null) {
          goTo(nextId);
        } else {
          const next = new URLSearchParams();
          next.set('sortBy', sortBy);
          next.set('order', order);
          setSearchParams(next, { replace: true });
        }
      },
    });
  };

  const handleAdd = (): void => {
    if (!confirmLeave()) return;
    // REQ-20260421009 §1:進新增模式時清除搜尋 context
    const next = new URLSearchParams();
    next.set('mode', 'new');
    next.set('sortBy', sortBy);
    next.set('order', order);
    setSearchParams(next);
  };

  /**
   * 「取消」真正執行的動作(REQ-20260916004)。
   *
   * - **新增模式**:整張表單本來就沒有對應的資料列,退回剛剛看的那一筆最直覺;
   *   沒有「剛剛那一筆」(一進來就按新增)就只留 sortBy / order,
   *   交給既有的 auto-load effect 抓第一筆。
   * - **檢視模式**:走 `formRef.current.reset()` —— `CustomerFormHandle` 的 reset()
   *   是 `reset(initialValues.current)` 有參數版,會還原 RHF 值與電話 / 傳真槽位。
   *   (廠商那邊沒有這支,理由見 REQ-20260803002,那邊改走 key 重新掛載。)
   *
   * 兩條路徑都順手把 mutation 的錯誤 banner 清掉 —— 取消之後畫面上還掛著
   * 「建立失敗:客戶編號已被使用」很奇怪,而且那條訊息會讓欄位一直紅框。
   */
  const doCancel = (): void => {
    createMutation.reset();
    updateMutation.reset();
    setDirty(false);
    if (mode === 'new') {
      const back = lastViewedIdRef.current;
      if (back != null) {
        goTo(back);
      } else {
        const next = new URLSearchParams();
        next.set('sortBy', sortBy);
        next.set('order', order);
        setSearchParams(next);
      }
      return;
    }
    formRef.current?.reset();
  };

  /**
   * 有未儲存變更才問一次。用 ConfirmDialog 不用 window.confirm ——
   * REQ-20260903009 / REQ-20260907004 立下的規矩。
   * 既有的 confirmLeave()(上一筆 / 下一筆 / 新增 / 搜尋挑選的離開攔截)是另一條路徑,
   * 本次刻意沒有動它。
   */
  const handleCancel = (): void => {
    if (!dirty) {
      doCancel();
      return;
    }
    setCancelConfirm(true);
  };

  const handlePickSearchResult = (
    pickedId: number,
    field: string | null,
    opts?: { interviewId?: number; q?: string },
  ): void => {
    if (!confirmLeave()) return;
    const next = new URLSearchParams(searchParams);
    next.delete('mode');
    next.set('id', String(pickedId));
    // 搜尋欄位同時成為翻頁排序 context(附件搜尋模式 field=null,不動 sortBy)
    if (field && (CUSTOMER_SORTABLE_FIELDS as readonly string[]).includes(field)) {
      next.set('sortBy', field);
      next.set('order', 'asc');
    }
    // REQ-20260421009 §1:客戶欄位搜尋模式 → 寫入 field + q 建立 scoped 翻頁 context
    if (field && opts?.q && (CUSTOMER_SEARCHABLE_FIELDS as readonly string[]).includes(field)) {
      next.set('field', field);
      next.set('q', opts.q);
    } else {
      // 附件模式或無 q 時清掉舊 context,避免污染
      next.delete('field');
      next.delete('q');
    }
    if (opts?.interviewId) {
      next.set('interviewId', String(opts.interviewId));
    } else {
      next.delete('interviewId');
    }
    setSearchParams(next);
    setSearchOpen(false);
  };

  const handleClearSearchContext = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('field');
    next.delete('q');
    setSearchParams(next);
  };

  // REQ-20260803001:後端 409 的訊息會指名欄位(customers.service.ts mapPrismaError),
  // 用關鍵字挑出要標紅框的那一欄;Banner 仍照舊顯示,兩者並存。
  const saveError = createMutation.error ?? updateMutation.error;
  const saveErrorMessage = saveError ? getApiErrorMessage(saveError, '未知錯誤') : null;
  const conflictField: 'cCode' | 'tax' | null = saveErrorMessage?.includes('客戶編號')
    ? 'cCode'
    : saveErrorMessage?.includes('統一編號')
      ? 'tax'
      : null;

  const submitting = createMutation.isPending || updateMutation.isPending;
  const canSave = mode === 'new' || (mode === 'view' && canWrite);
  const canDelete = mode === 'view' && canWrite && id != null;

  const titleText =
    mode === 'new' ? '新增客戶' : current ? current.simpleName : '客戶';
  const showTitle =
    mode === 'new' || !customerQuery.isLoading;

  return (
    <section className="grid gap-4">
      <div
        className="flex items-center justify-between gap-3 flex-wrap border-b pb-3"
        data-testid="console-header"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {showTitle ? (
            <>
              <h1 className="text-xl font-semibold truncate">{titleText}</h1>
              {mode === 'view' && current?.cCode ? (
                <span className="font-mono text-slate-500 text-sm shrink-0">
                  {current.cCode}
                </span>
              ) : null}
              {dirty ? (
                <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 shrink-0">
                  未儲存
                </span>
              ) : null}
              {hasSearchContext && searchField ? (
                <span
                  className="inline-flex items-center gap-1 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 shrink-0"
                  data-testid="search-context-chip"
                >
                  已過濾:{CUSTOMER_FIELD_LABELS[searchField]}={searchQ}
                  <button
                    type="button"
                    onClick={handleClearSearchContext}
                    className="text-blue-700 hover:text-blue-900"
                    aria-label="清除搜尋條件"
                    data-testid="btn-clear-search-context"
                  >
                    ✕
                  </button>
                </span>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="shrink-0">
        <Toolbar
          canPrev={mode === 'view' && prevQuery.data?.id != null}
          canNext={mode === 'view' && nextQuery.data?.id != null}
          canSave={canSave}
          canDelete={canDelete}
          dirty={dirty}
          submitting={submitting}
          pinned={mode === 'view' && !!pinnedQuery.data}
          canPin={mode === 'view' && id != null}
          onTogglePin={
            mode === 'view' && id != null
              ? () => togglePinMutation.mutate(!pinnedQuery.data)
              : undefined
          }
          onPrev={handlePrev}
          onNext={handleNext}
          onSearch={() => setSearchOpen(true)}
          onSave={handleSave}
          onCancel={handleCancel}
          canCancel={mode === 'new' || dirty}
          onDelete={openDeleteDialog}
          onAdd={handleAdd}
        />
        </div>
      </div>

      {submitting ? <div className="text-slate-500 text-sm">儲存中…</div> : null}
      {createMutation.isError ? (
        <Banner kind="error">
          建立失敗：
          {getApiErrorMessage(createMutation.error, '未知錯誤')}
        </Banner>
      ) : null}
      {updateMutation.isError ? (
        <Banner kind="error">
          更新失敗：
          {getApiErrorMessage(updateMutation.error, '未知錯誤')}
        </Banner>
      ) : null}

      {mode === 'view' && customerQuery.isLoading ? (
        <div className="text-slate-500">載入中...</div>
      ) : mode === 'view' && (customerQuery.isError || !current) ? (
        <div role="alert" className="text-red-600">
          找不到客戶或無存取權限
        </div>
      ) : (
        <>
          <CustomerForm
            ref={formRef}
            defaultValues={mode === 'new' ? undefined : current}
            ownerId={current?.ownerId ?? me?.id ?? 0}
            readOnly={readOnly}
            onSubmit={handleSubmit}
            onDirtyChange={setDirty}
            conflictField={conflictField}
            conflictMessage={saveErrorMessage}
            autoSuggestCode={!readOnly}
          />

          {mode === 'view' && current ? (
            <div>
              <div className="flex justify-end gap-1 border-b mt-2" data-testid="console-tabs">
                <TabButton
                  active={activeTab === 'reminders'}
                  onClick={() => setActiveTab('reminders')}
                  testId="tab-reminders"
                  tone={
                    openRemindersQuery.data && openRemindersQuery.data.length > 0
                      ? 'alert'
                      : 'default'
                  }
                >
                  🔔 提醒事項
                  {openRemindersQuery.data != null ? ` (${openRemindersQuery.data.length})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'interviews'}
                  onClick={() => setActiveTab('interviews')}
                  testId="tab-interviews"
                >
                  訪談紀錄
                  {interviewsTotalQuery.data != null ? ` (${interviewsTotalQuery.data})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'inventory'}
                  onClick={() => setActiveTab('inventory')}
                  testId="tab-inventory"
                >
                  存貨紀錄
                  {inventoryTotalQuery.data != null ? ` (${inventoryTotalQuery.data})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'line'}
                  onClick={() => setActiveTab('line')}
                  testId="tab-line"
                >
                  LINE 綁定
                  {lineContactsQuery.data != null ? ` (${lineContactsQuery.data.length})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'ar'}
                  onClick={() => setActiveTab('ar')}
                  testId="tab-ar"
                >
                  應收沖帳
                  {arTotalQuery.data != null ? ` (${arTotalQuery.data})` : ''}
                </TabButton>
              </div>
              <div className="pt-3">
                {activeTab === 'reminders' ? (
                  <ReminderPanel
                    ownerType="customer"
                    ownerId={current.id}
                    customerOwnerId={current.ownerId}
                  />
                ) : activeTab === 'interviews' ? (
                  <InterviewTimeline
                    ownerId={current.id}
                    ownerType="customer"
                    customerOwnerId={current.ownerId}
                    highlightInterviewId={highlightInterviewId}
                    defaultContact={current.contact}
                    onHighlightDone={() => {
                      const next = new URLSearchParams(searchParams);
                      next.delete('interviewId');
                      setSearchParams(next, { replace: true });
                    }}
                  />
                ) : activeTab === 'inventory' ? (
                  <InventoryTimeline
                    customerId={current.id}
                    customerOwnerId={current.ownerId}
                  />
                ) : activeTab === 'line' ? (
                  <CustomerLineContactsPanel customerId={current.id} />
                ) : (
                  <CustomerArPanel customerId={current.id} />
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={handlePickSearchResult}
      />

      {mode === 'view' && current ? (
        <DeleteCustomerDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleConfirmDelete}
          simpleName={current.simpleName}
          cCode={current.cCode}
          submitting={deleteMutation.isPending}
          errorMessage={
            deleteMutation.isError
              ? getApiErrorMessage(deleteMutation.error, '刪除失敗')
              : null
          }
        />
      ) : null}

      <ConfirmDialog
        open={cancelConfirm}
        title="放棄未儲存的變更?"
        message="剛剛輸入但還沒儲存的內容會全部消失,已經存進系統的資料不受影響。"
        confirmLabel="放棄變更"
        cancelLabel="繼續編輯"
        tone="danger"
        onConfirm={doCancel}
        onClose={() => setCancelConfirm(false)}
      />
    </section>
  );
}

// ────────────────────────────────────────
// 子元件：工具列
// ────────────────────────────────────────

interface ToolbarProps {
  canPrev: boolean;
  canNext: boolean;
  canSave: boolean;
  canDelete: boolean;
  dirty: boolean;
  submitting: boolean;
  pinned: boolean;
  canPin: boolean;
  onTogglePin?: (() => void) | undefined;
  onPrev: () => void;
  onNext: () => void;
  onSearch: () => void;
  onSave: () => void;
  /** REQ-20260916004:放棄這次的輸入。新增模式或有未儲存變更時才可按 */
  onCancel: () => void;
  canCancel: boolean;
  onDelete: () => void;
  onAdd: () => void;
}

function Toolbar(props: ToolbarProps): JSX.Element {
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="console-toolbar"
    >
      {props.canPin && props.onTogglePin ? (
        <button
          type="button"
          onClick={props.onTogglePin}
          className={`border rounded px-2.5 py-1 text-sm ${
            props.pinned
              ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
              : 'hover:bg-slate-100'
          }`}
          title={props.pinned ? '取消釘選' : '釘選此客戶'}
          aria-pressed={props.pinned}
          data-testid="btn-toggle-pin"
        >
          {props.pinned ? '📌 已釘' : '📌 釘選'}
        </button>
      ) : null}
      <button
        type="button"
        onClick={props.onAdd}
        className="border rounded px-2.5 py-1 text-sm hover:bg-slate-100"
        data-testid="btn-add-customer"
      >
        + 新增
      </button>
      <button
        type="button"
        onClick={props.onSave}
        disabled={!props.canSave || props.submitting}
        className={
          props.dirty
            ? 'bg-blue-600 text-white rounded px-2.5 py-1 text-sm hover:bg-blue-700 disabled:opacity-50'
            : 'border rounded px-2.5 py-1 text-sm hover:bg-slate-100 disabled:opacity-50'
        }
        data-testid="btn-save"
      >
        儲存
      </button>
      <button
        type="button"
        onClick={props.onDelete}
        disabled={!props.canDelete}
        className="border rounded px-2.5 py-1 text-sm text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-40"
        data-testid="btn-delete"
      >
        刪除
      </button>
      <button
        type="button"
        onClick={props.onCancel}
        disabled={!props.canCancel}
        className="border rounded px-2.5 py-1 text-sm hover:bg-slate-100 disabled:opacity-40"
        data-testid="btn-cancel"
      >
        取消
      </button>
      <button
        type="button"
        onClick={props.onPrev}
        disabled={!props.canPrev}
        className="border rounded px-2.5 py-1 text-sm hover:bg-slate-100 disabled:opacity-40"
        data-testid="btn-prev"
      >
        ← 上一筆
      </button>
      <button
        type="button"
        onClick={props.onNext}
        disabled={!props.canNext}
        className="border rounded px-2.5 py-1 text-sm hover:bg-slate-100 disabled:opacity-40"
        data-testid="btn-next"
      >
        下一筆 →
      </button>
      <button
        type="button"
        onClick={props.onSearch}
        className="border border-blue-600 text-blue-600 rounded px-2.5 py-1 text-sm hover:bg-blue-50"
        data-testid="btn-search"
      >
        搜尋
      </button>
    </div>
  );
}

function Banner(props: {
  kind: 'error' | 'info';
  children: React.ReactNode;
}): JSX.Element {
  const cls =
    props.kind === 'error'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <div role="alert" className={`text-sm rounded border p-2 ${cls}`}>
      {props.children}
    </div>
  );
}
